import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { authService, RefreshAuthError } from '@/services/auth.service';
import { tokenService } from '@/services/token.service';
import { eventBusService } from '@/services/event-bus.service';
import type {
  AuthContextType,
  AuthUser,
  LoginCredentials,
  SendOtpCredentials,
  VerifyOtpCredentials,
  SendOtpResponse,
} from '@/types';

// ============================================================================
// CONSTANTS
// ============================================================================

// Check token every 30 seconds
const TOKEN_CHECK_INTERVAL = 30 * 1000;
// Refresh token 5 minutes before expiry
const REFRESH_BUFFER_SECONDS = 300;

/**
 * Resultado tipado del refresh para que la UI pueda decidir:
 *   - ok: todo bien, sesión renovada
 *   - auth: backend dijo 401/403 → la sesión fue revocada, logout ya
 *   - network: backend inalcanzable o 5xx → mantenemos sesión y
 *     reintentamos más tarde. NUNCA logout por esto.
 *   - no-token: no hay cookies, no tiene sentido intentar
 */
type RefreshResult =
  | { ok: true }
  | { ok: false; reason: 'auth' | 'network' | 'no-token' };

// ============================================================================
// CONTEXT
// ============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// ============================================================================
// PROVIDER
// ============================================================================

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  // With httpOnly cookies, we no longer store tokens in React state.
  // isAuthenticated is derived from user + CSRF cookie presence.
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const isRefreshingRef = useRef(false);

  const performTokenRefresh = useCallback(async (): Promise<RefreshResult> => {
    if (!tokenService.hasToken()) {
      return { ok: false, reason: 'no-token' };
    }

    try {
      await authService.refreshAccessToken();
      return { ok: true };
    } catch (err) {
      if (err instanceof RefreshAuthError) {
        return { ok: false, reason: 'auth' };
      }
      // RefreshNetworkError o cualquier otro error no-auth → network
      return { ok: false, reason: 'network' };
    }
  }, []);

  const clearSessionAndRedirect = useCallback(() => {
    tokenService.clearTokens();
    authService.clearUserData();
    authService.clearLastSuccessfulRefresh();
    setUser(null);
    setIsAuthenticated(false);
    window.location.href = '/auth/login';
  }, []);

  // Proactive token refresh: corre cada 30s si estamos autenticados.
  // Solo expulsa al usuario si el backend devuelve 401/403 explícito.
  // Backend caído / 5xx / network → reintentamos silenciosamente.
  const refreshTokenProactively = useCallback(async () => {
    if (isRefreshingRef.current || !tokenService.hasToken()) {
      return;
    }

    const expiry = tokenService.getTokenExpiry();
    if (!expiry) return;

    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = expiry - now;

    if (timeUntilExpiry <= REFRESH_BUFFER_SECONDS) {
      isRefreshingRef.current = true;
      try {
        const result = await performTokenRefresh();
        if (!result.ok && result.reason === 'auth') {
          clearSessionAndRedirect();
        }
        // network / no-token → el interval reintentará en 30s
      } finally {
        isRefreshingRef.current = false;
      }
    }
  }, [performTokenRefresh, clearSessionAndRedirect]);

  // Set up proactive refresh interval
  useEffect(() => {
    if (!isAuthenticated) return;

    refreshTokenProactively();
    const intervalId = setInterval(
      refreshTokenProactively,
      TOKEN_CHECK_INTERVAL,
    );

    return () => clearInterval(intervalId);
  }, [isAuthenticated, refreshTokenProactively]);

  // Real-time session invalidation — suscripción al bus unificado.
  // El bus abre la conexión SSE (en EventBusProvider, hijo de
  // AuthProvider); acá solo escuchamos el evento. Usamos el service
  // singleton (no `useEventBus()`) porque AuthProvider está por fuera
  // del EventBusProvider en el árbol React.
  //
  // Filtro por `deviceId`: el bus emite a `user:<id>`, así que TODAS
  // las pestañas/dispositivos de este usuario reciben el evento. Solo
  // el device cuya sesión fue revocada debe hacer logout — sin este
  // filtro, revocar una sesión cerraba todas las tabs del usuario.
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubscribe = eventBusService.subscribe('session_revoked', (raw) => {
      const data = raw as { deviceId?: string; timestamp?: string };
      const myDeviceId = tokenService.getExistingDeviceId();
      if (!myDeviceId || !data.deviceId || data.deviceId !== myDeviceId) return;

      tokenService.clearTokens();
      authService.clearUserData();
      setUser(null);
      setIsAuthenticated(false);
      window.location.href = '/auth/login?reason=session_revoked';
    });

    return unsubscribe;
  }, [isAuthenticated]);

  // Initialize auth state
  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        const hasCookies = tokenService.hasToken();
        const isExpired = tokenService.isTokenExpired();

        if (hasCookies && isExpired) {
          // Intentamos refresh al boot. Resultado:
          //   ok          → cargamos user y seguimos
          //   auth        → cookies inválidas/revocadas, unauth silencioso
          //   network     → backend caído. Mantenemos cache del user y
          //                 dejamos que el interval reintente — NO logout.
          isRefreshingRef.current = true;
          const result = await performTokenRefresh();
          isRefreshingRef.current = false;

          if (result.ok) {
            const userData = authService.getUser();
            if (isMounted) {
              setUser(userData);
              setIsAuthenticated(true);
              setError(null);
            }
          } else if (result.reason === 'auth') {
            tokenService.clearTokens();
            authService.clearUserData();
            authService.clearLastSuccessfulRefresh();
            if (isMounted) {
              setUser(null);
              setIsAuthenticated(false);
              setError(null);
            }
          } else {
            // network / no-token → mantenemos la sesión cacheada
            const userData = authService.getUser();
            if (isMounted && userData) {
              setUser(userData);
              setIsAuthenticated(true);
              setError(null);
            } else if (isMounted) {
              // Sin user cacheado no hay nada que mostrar, pero tampoco
              // forzamos login: el interval intentará refrescar luego.
              setUser(null);
              setIsAuthenticated(false);
              setError(null);
            }
          }
        } else if (hasCookies && !isExpired) {
          const userData = authService.getUser();
          if (isMounted) {
            setUser(userData);
            setIsAuthenticated(true);
            setError(null);
          }
        } else {
          if (isMounted) {
            setUser(null);
            setIsAuthenticated(false);
            setError(null);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(
            err instanceof Error
              ? err
              : new Error('Auth initialization failed'),
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, [performTokenRefresh]);

  // Sign in (legacy)
  const signIn = useCallback(async (credentials: LoginCredentials) => {
    try {
      setError(null);
      const response = await authService.signIn(credentials);
      setUser(response.user);
      setIsAuthenticated(true);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Sign in failed');
      setError(error);
      throw error;
    }
  }, []);

  // Send OTP (Step 1)
  const sendOtp = useCallback(
    async (credentials: SendOtpCredentials): Promise<SendOtpResponse> => {
      try {
        setError(null);
        return await authService.sendOtp(credentials);
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error('No se pudo enviar el código');
        setError(error);
        throw error;
      }
    },
    [],
  );

  // Verify OTP (Step 2). Returns the discriminated mode so the caller
  // (LoginPage) can route to the TOTP challenge page when required.
  const verifyOtp = useCallback(
    async (credentials: VerifyOtpCredentials & { trustDevice?: boolean }) => {
      try {
        setError(null);
        const response = await authService.verifyOtp(credentials);
        if (response.mode !== 'totp_required') {
          setUser(response.user);
          setIsAuthenticated(true);
        }
        return response;
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error('Código incorrecto o expirado');
        setError(error);
        throw error;
      }
    },
    [],
  );

  // Step 3a — TOTP challenge completion
  const verifyTotp = useCallback(
    async (input: {
      challengeToken: string;
      code: string;
      trustDevice?: boolean;
    }) => {
      try {
        setError(null);
        const response = await authService.verifyTotp(input);
        setUser(response.user);
        setIsAuthenticated(true);
        return response;
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Código TOTP incorrecto');
        setError(error);
        throw error;
      }
    },
    [],
  );

  // Step 3b — recovery code fallback
  const useRecoveryCode = useCallback(
    async (input: {
      challengeToken: string;
      recoveryCode: string;
      trustDevice?: boolean;
    }) => {
      try {
        setError(null);
        const response = await authService.useRecoveryCode(input);
        setUser(response.user);
        setIsAuthenticated(true);
        return response;
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error('Código de recuperación inválido');
        setError(error);
        throw error;
      }
    },
    [],
  );

  // Refresh profile from /auth/me after mutating totp state
  const refreshProfile = useCallback(async () => {
    try {
      const u = await authService.getCurrentUser();
      if (u) setUser(u);
    } catch {
      /* ignore — /auth/me transient failures are non-fatal here */
    }
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      await authService.signOut();
      setUser(null);
      setIsAuthenticated(false);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Sign out failed');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextType = useMemo(
    () => ({
      user,
      // Keep accessToken/refreshToken for interface compatibility, but as null
      accessToken: isAuthenticated ? 'httponly' : null,
      refreshToken: isAuthenticated ? 'httponly' : null,
      isLoading,
      isAuthenticated,
      error,
      signIn,
      sendOtp,
      verifyOtp,
      verifyTotp,
      useRecoveryCode,
      refreshProfile,
      signOut,
      clearError,
    }),
    [
      user,
      isAuthenticated,
      isLoading,
      error,
      signIn,
      sendOtp,
      verifyOtp,
      verifyTotp,
      useRecoveryCode,
      refreshProfile,
      signOut,
      clearError,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================================================
// HOOK
// ============================================================================

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
